/**
 * Planner stage components — Chooser, Quick Launch, Discovery, Template,
 * Hybrid, Editor (Timeline + Graph + Kanban + Inspector), Review.
 *
 * Visual fidelity ported from `Design/planner.jsx` (771 lines of
 * React.createElement). Translated to JSX/TSX, wired to the live
 * backend (decompose, discovery sessions, plays approve+start), and
 * driven by the persisted `usePlannerStore` so navigating away mid-flow
 * doesn't lose state.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../../../api/client.js";
import { usePlannerSession } from "../../../state/usePlannerStore.js";
import { OutcomeHeader } from "../../../components/OutcomeHeader.js";
import { useTemplates, useTemplatePreview, type TemplateMeta } from "../../../hooks/useTemplates.js";
import { usePlanIt, useFromTemplate, type PlanItResult } from "../../../hooks/useRoadmap.js";
import { useStartDiscoverySession } from "../../../hooks/usePlanner.js";
import { complete as completeDiscovery, postTurn } from "../../../api/discovery.js";
import { PLANNER_FIXTURE } from "./fixtures.js";
import { projectDraft } from "./projector.js";
import type { PlannerData, PlannerPlaybook, PlannerStep } from "./types.js";
import { StepEditModal } from "../../../components/StepEditModal.js";
import { AgentMatchModal } from "../../../components/AgentMatchModal.js";

interface StageBaseProps {
  workspaceId: string;
  entryId: string;
  initiativeTitle: string;
  playId?: string;
}

/**
 * `overrideTemplateId` is encoded by TemplatesCatalog.templateKey as
 * "industry/trade/archetype/version". Split it back so we can call the
 * preview endpoint. Returns null when the shape doesn't match.
 */
function parseTemplateKey(
  key: string | null | undefined,
): { industry: string; trade: string; archetype: string; version: string } | null {
  if (!key) return null;
  const parts = key.split("/");
  if (parts.length < 4) return null;
  return {
    industry: parts[0]!,
    trade: parts[1]!,
    archetype: parts[2]!,
    version: parts[3]!,
  };
}

/**
 * Helpful indirection: most stages need the persisted session helpers
 * plus a small data bag (initiative meta, fallback fixture, projected
 * draft). This factory keeps the call sites tidy.
 */
function useStageContext({
  workspaceId,
  entryId,
  initiativeTitle,
}: StageBaseProps) {
  const { session, patch, reset } = usePlannerSession(entryId);
  const data: PlannerData = useMemo(() => {
    const fixture = PLANNER_FIXTURE;
    const draftFromBackend = session.draftPlay;
    const fullPlaybook: PlannerPlaybook = draftFromBackend ?? {
      ...fixture.fullPlaybook,
      title: initiativeTitle || fixture.fullPlaybook.title,
    };
    return {
      ...fixture,
      initiative: {
        ...fixture.initiative,
        title: initiativeTitle || fixture.initiative.title,
      },
      fullPlaybook,
    };
  }, [session.draftPlay, initiativeTitle]);

  return { session, patch, reset, data, workspaceId, entryId, initiativeTitle };
}

// ────────────────────────────────────────────────────────────────────────────
// CHOOSER

/**
 * Two-question outcome capture sitting above the path picker. Optional
 * but prominent — pre-fills Discovery's Phase 1 if the user chooses
 * that path, and is the sole outcome source for Template / Quick Launch
 * which skip Discovery entirely. Writes straight into the planner
 * session so the OutcomeHeader pins it across Editor / Review.
 */
function OutcomeCapture({
  outcomeStatement,
  successMetrics,
  onChange,
}: {
  outcomeStatement: string | null;
  successMetrics: Array<{ label: string; target: string; measurement_window?: string }>;
  onChange: (next: {
    outcomeStatement: string | null;
    successMetrics: Array<{ label: string; target: string; measurement_window?: string }>;
  }) => void;
}) {
  const m0 = successMetrics[0] ?? { label: "", target: "", measurement_window: "" };
  function updateMetric(field: "label" | "target" | "measurement_window", val: string) {
    const next = [...successMetrics];
    next[0] = { ...m0, [field]: val };
    // Drop the row entirely if the user blanked out both label + target
    // so an empty metric doesn't stick around forever.
    const cleaned = next.filter((x) => (x.label?.length ?? 0) > 0 || (x.target?.length ?? 0) > 0);
    onChange({ outcomeStatement, successMetrics: cleaned });
  }
  return (
    <div
      style={{
        background: "color-mix(in srgb, var(--accent, #6366f1) 4%, transparent)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 18,
      }}
      data-testid="outcome-capture"
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 8 }}>
        Define the outcome (recommended)
      </div>
      <input
        data-testid="outcome-input"
        value={outcomeStatement ?? ""}
        onChange={(e) =>
          onChange({
            outcomeStatement: e.target.value.length > 0 ? e.target.value : null,
            successMetrics,
          })
        }
        placeholder="What outcome are you trying to drive? e.g. Lift wholesale GM by 2.8 pts in Q3."
        style={{
          width: "100%",
          padding: "8px 10px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: "var(--bg)",
          fontSize: 14,
          marginBottom: 10,
        }}
      />
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 4 }}>
        Key metric (optional)
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <input
          data-testid="metric-label"
          value={m0.label}
          onChange={(e) => updateMetric("label", e.target.value)}
          placeholder="Metric (e.g. Wholesale GM)"
          style={{
            flex: "2 1 200px",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--bg)",
            fontSize: 12,
          }}
        />
        <input
          data-testid="metric-target"
          value={m0.target}
          onChange={(e) => updateMetric("target", e.target.value)}
          placeholder="Target (e.g. +2.8 pts)"
          style={{
            flex: "1 1 140px",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--bg)",
            fontSize: 12,
          }}
        />
        <input
          data-testid="metric-window"
          value={m0.measurement_window ?? ""}
          onChange={(e) => updateMetric("measurement_window", e.target.value)}
          placeholder="By when (e.g. Q3)"
          style={{
            flex: "1 1 100px",
            padding: "6px 8px",
            border: "1px solid var(--border)",
            borderRadius: 6,
            background: "var(--bg)",
            fontSize: 12,
          }}
        />
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-3)" }}>
        Filled here → carried into the plan + pinned at the top of the
        Editor and the running play. Discovery starts from this too if
        you pick that path.
      </div>
    </div>
  );
}

export function ChooserStage(props: StageBaseProps) {
  const { patch, data, session } = useStageContext(props);
  const navigate = useNavigate();
  return (
    <div className="main-inner planner-chooser" data-testid="planner-chooser">
      <div className="planner-crumb">
        <a
          onClick={() => navigate(`/workspaces/${props.workspaceId}/inbox`)}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer" }}
        >
          ← Back
        </a>
        {" · "}
        <span className="mode-tag">XamunPlanner</span>
      </div>
      <div className="planner-h">
        <div className="section-eyebrow">PLAN A NEW PLAY</div>
        <div className="planner-title">{data.initiative.title}</div>
        <div className="planner-sub">
          <span className="pill purple">{data.initiative.lineage}</span>
          {" · "}
          {data.initiative.objective}
        </div>
      </div>

      <OutcomeCapture
        outcomeStatement={session.outcomeStatement}
        successMetrics={session.successMetrics}
        onChange={(next) => patch(next)}
      />

      <div className="planner-pick">
        <div className="pick-h">Choose your planning mode</div>
        <div className="pick-sub">
          All paths converge on the same Editor. Quick Launch is the daily
          path; Discovery is the deepest.
        </div>

        <div
          className="pick-quick"
          onClick={() => patch({ stage: "quick" })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") patch({ stage: "quick" });
          }}
          data-testid="path-quick"
        >
          <div className="pick-quick-l">
            <div className="pick-quick-kind">QUICK LAUNCH · DAILY PATH</div>
            <div className="pick-quick-t">Glance, confirm, launch</div>
            <div className="pick-quick-d">
              Skim XAMI&apos;s draft from the inbox lineage — confirm
              assumptions, fix one or two, launch in under 5 minutes. Falls
              back to the Editor if you want to go deeper.
            </div>
          </div>
          <div className="pick-quick-r">
            <div className="pick-quick-m">
              <span>12 steps</span> · <span>~6 weeks</span> · <span>3 gates</span>
            </div>
            <span className="btn primary">Quick launch →</span>
          </div>
        </div>

        <div className="pick-or">
          <span className="pick-or-line" />
          <span>or design from scratch</span>
          <span className="pick-or-line" />
        </div>

        <div className="pick-grid">
          <div
            className="pick-card"
            onClick={() => patch({ stage: "template" })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") patch({ stage: "template" });
            }}
            data-testid="path-template"
          >
            <div className="pick-kind">PATH A · TEMPLATE-DRIVEN</div>
            <div className="pick-t">Template</div>
            <div className="pick-d">
              Compose from Industry × Trade × Project archetype. Fast —
              Playbook in ~30s.
            </div>
            <div className="pick-meta">
              ~30 seconds · best when archetype match is high
            </div>
            <div className="pick-best">Best for: Pax8/MSP-led, well-known archetypes</div>
          </div>
          <div
            className="pick-card primary"
            onClick={() => patch({ stage: "discovery" })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") patch({ stage: "discovery" });
            }}
            data-testid="path-discovery"
          >
            <div className="pick-kind">PATH B · DISCOVERY-DRIVEN</div>
            <div className="pick-t">Discovery</div>
            <div className="pick-d">
              Conversational interview. XAMI builds the Playbook from your
              context — same engine as Software Factory.
            </div>
            <div className="pick-meta">
              ~15–20 min · best for novel or context-rich initiatives
            </div>
            <div className="pick-best">Best for: novel plays, capturing tacit context</div>
            <div className="pick-reco">Recommended</div>
          </div>
          <div
            className="pick-card"
            onClick={() => patch({ stage: "hybrid" })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") patch({ stage: "hybrid" });
            }}
            data-testid="path-hybrid"
          >
            <div className="pick-kind">PATH C · HYBRID</div>
            <div className="pick-t">Template + Discovery refinement</div>
            <div className="pick-d">
              Start from a template, then refine via interview. Highest-quality
              path; best Pattern Library calibration.
            </div>
            <div className="pick-meta">
              ~10 min · best for important plays where archetype is known
            </div>
            <div className="pick-best">Best for: high-stakes plays in known archetypes</div>
          </div>
        </div>
      </div>

      <div className="planner-foot-attr">
        Powered by Xamun Discovery · same interview engine as Software Factory
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// QUICK LAUNCH

export function QuickStage(props: StageBaseProps) {
  const { patch, data, workspaceId, entryId, session } = useStageContext(props);
  const planIt = usePlanIt(workspaceId);
  const ql = data.quickLaunch;
  const full = data.fullPlaybook;

  const onLaunch = () => {
    if (!entryId) {
      // No real backend entry — fixture-only preview. Just jump to review.
      patch({ stage: "review" });
      return;
    }
    planIt.mutate(
      {
        entryId,
        ...(session.outcomeStatement !== null
          ? { outcomeStatement: session.outcomeStatement }
          : {}),
        ...(session.successMetrics.length > 0
          ? { successMetrics: session.successMetrics }
          : {}),
      },
      {
        onSuccess: (r: PlanItResult) => {
          if (r.kind === "draft_play") {
            const projected = projectDraft(
              r.play as { id: string; steps?: { id: string }[]; edges?: { from: string; to: string }[] },
              { initiativeTitle: data.initiative.title },
            );
            patch({
              stage: "review",
              draftPlay: projected,
              decompositionInputId: r.decompositionInputId,
            });
          } else {
            // Discovery required — switch path.
            patch({ stage: "discovery" });
          }
        },
      },
    );
  };

  return (
    <div className="planner-quick" data-testid="planner-quick">
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={() => patch({ stage: "chooser" })}
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">
          Quick launch ·{" "}
          <span style={{ color: "var(--text-3)" }}>{data.initiative.title}</span>
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn"
          onClick={() => patch({ stage: "editor" })}
          data-testid="quick-open-editor"
        >
          Open in Editor
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={onLaunch}
          disabled={planIt.isPending}
          data-testid="quick-launch"
        >
          {planIt.isPending ? "Decomposing…" : "Review & launch →"}
        </button>
      </div>

      <div className="ql-body">
        <div className="ql-hero">
          <div className="section-eyebrow">XAMI DRAFT · READY TO LAUNCH</div>
          <div className="ql-hero-t">{data.initiative.title}</div>
          <div className="ql-hero-s">
            Drafted from XI lineage —{" "}
            <span className="pill purple">{data.initiative.lineage}</span>
            {" · archetype: "}
            <b>{ql.template}</b>
          </div>
          <div className="ql-hero-m">
            <div className="ql-stat">
              <div className="ql-stat-v">{ql.stepCount}</div>
              <div className="ql-stat-l">Steps</div>
            </div>
            <div className="ql-stat">
              <div className="ql-stat-v">{ql.duration}</div>
              <div className="ql-stat-l">Duration</div>
            </div>
            <div className="ql-stat">
              <div className="ql-stat-v">{ql.gates}</div>
              <div className="ql-stat-l">Decision gates</div>
            </div>
            <div className="ql-stat">
              <div className="ql-stat-v">{full.kpiTarget}</div>
              <div className="ql-stat-l">Target</div>
            </div>
          </div>
        </div>

        <div className="ql-cols">
          <div className="ql-col">
            <div className="ql-col-h">
              <span>Assumptions XAMI made</span>
              <span className="ql-col-s">Skim, fix anything wrong, launch</span>
            </div>
            <div className="ql-assume">
              {ql.assumptions.map((a, i) => (
                <div className="ql-assume-i" key={i}>
                  <span className="ql-check">✓</span>
                  <span className="ql-assume-t">{a}</span>
                  <a className="ql-edit">Edit</a>
                </div>
              ))}
            </div>
          </div>
          <div className="ql-col">
            <div className="ql-col-h">
              <span>Stakeholders</span>
            </div>
            <div className="ql-stakes">
              {ql.stakeholders.map((s, i) => (
                <div className="ql-stake" key={i}>
                  <div className="ql-stake-av">
                    {s.who
                      .split(" ")
                      .map((p) => p[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                  <div>
                    <div className="ql-stake-n">{s.who}</div>
                    <div className="ql-stake-r">{s.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ql-col-h" style={{ marginTop: 18 }}>
              <span>Capability gaps</span>
            </div>
            <div className="ql-gaps">
              {ql.gaps.map((g, i) => (
                <div className="ql-gap" key={i}>
                  <div>
                    <span className="ql-gap-i">⚠</span>
                    <b>{g.capability}</b>
                  </div>
                  <div className="ql-gap-m">{g.impact}</div>
                  <a className="ql-gap-a">+ Bind agent</a>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ql-foot">
          <div className="ql-foot-l">
            <div className="ql-foot-h">Looks right?</div>
            <div className="ql-foot-s">
              You can refine in the Editor or launch directly. Either way the
              play moves to Hands Ops.
            </div>
          </div>
          <div className="ql-foot-r">
            <button
              type="button"
              className="btn"
              onClick={() => patch({ stage: "editor" })}
            >
              Open in Editor
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={onLaunch}
              disabled={planIt.isPending}
            >
              {planIt.isPending ? "Decomposing…" : "Review & launch →"}
            </button>
          </div>
        </div>

        {planIt.error && (
          <div
            data-testid="quick-error"
            style={{ marginTop: 12, color: "var(--danger)", fontSize: 12 }}
          >
            {planIt.error.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// DISCOVERY (with materializing canvas)

interface DiscoveryStageProps extends StageBaseProps {
  /** When true, render the hybrid topbar variant. */
  hybrid?: boolean;
}

export function DiscoveryStage(props: DiscoveryStageProps) {
  const { patch, data, workspaceId, entryId, session } = useStageContext(props);
  const startSession = useStartDiscoverySession(workspaceId);
  const [mode, setMode] = useState<"voice" | "chat">("chat");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState(data.transcript);
  const draftSteps = data.draftPlaybook.steps;

  const sessionId = session.discoverySessionId;

  const ensureSession = async () => {
    if (sessionId || !entryId) return sessionId;
    const r = await startSession.mutateAsync({
      roadmap_entry_id: entryId,
      mode: "chat",
    });
    patch({ discoverySessionId: r.session.id });
    return r.session.id;
  };

  const onSend = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError(null);
    const userText = text;
    setText("");
    setTranscript((t) => [
      ...t,
      { who: "YOU", time: new Date().toLocaleTimeString(), text: userText },
    ]);
    try {
      const sid = await ensureSession();
      if (sid) {
        const r = await postTurn(sid, {
          turnId: `turn-${crypto.randomUUID()}`,
          userText,
        });
        setTranscript((t) => [
          ...t,
          { who: "XAMI", time: new Date().toLocaleTimeString(), text: r.assistantMessage },
        ]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onContinueToEditor = async () => {
    setBusy(true);
    setError(null);
    try {
      if (sessionId) {
        const r = await completeDiscovery(sessionId);
        const projected = projectDraft(
          r.draftPlay as { steps?: { id: string }[]; edges?: { from: string; to: string }[] } | null,
          { initiativeTitle: data.initiative.title },
        );
        patch({ stage: "editor", draftPlay: projected });
      } else {
        patch({ stage: "editor" });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="planner-discovery" data-testid="planner-discovery">
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={() => patch({ stage: "chooser" })}
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">
          {props.hybrid ? "Hybrid · " : ""}
          {data.initiative.title}
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn">Save draft</button>
        <button
          type="button"
          className="btn primary"
          onClick={onContinueToEditor}
          disabled={busy}
          data-testid="discovery-continue"
        >
          Continue to Editor →
        </button>
      </div>

      <div className="pd-body">
        <div className="pd-left">
          <div className="pd-mode">
            <button
              type="button"
              className={`pd-mode-btn ${mode === "voice" ? "active" : ""}`}
              onClick={() => setMode("voice")}
            >
              <span className="pd-mode-i">◉</span>
              Voice
            </button>
            <button
              type="button"
              className={`pd-mode-btn ${mode === "chat" ? "active" : ""}`}
              onClick={() => setMode("chat")}
            >
              <span className="pd-mode-i">✎</span>
              Text
            </button>
          </div>

          <div className="pd-cont">
            <div className="pd-cont-av">↻</div>
            <div className="pd-cont-txt">
              <span className="pd-cont-l">Click to continue conversation</span>
              <span className="pd-cont-s">
                {mode === "voice" ? "Voice mode · Gemini" : "Text mode"}
              </span>
            </div>
          </div>

          <div className="pd-prog">
            <div className="pd-prog-h">
              <span>Interview progress</span>
              <span>3 / 8</span>
            </div>
            {data.phases.map((p, i) => (
              <div className={`pd-phase pd-phase-${p.status}`} key={p.id}>
                <span className="pd-phase-n">
                  {p.status === "done" ? "✓" : i + 1}
                </span>
                <span className="pd-phase-l">{p.label}</span>
                {p.est && <span className="pd-phase-e">{p.est}</span>}
              </div>
            ))}
          </div>

          <div className="pd-tr-h">Transcript</div>
          <div className="pd-tr">
            {transcript.map((m, i) => (
              <div
                className={`pd-msg ${m.who === "YOU" ? "pd-msg-you" : "pd-msg-xami"}`}
                key={i}
              >
                <div className="pd-msg-h">
                  <span className="pd-msg-w">{m.who}</span>
                  <span className="pd-msg-t">{m.time}</span>
                </div>
                <div className="pd-msg-b">{m.text}</div>
              </div>
            ))}
          </div>

          {mode === "chat" && (
            <div style={{ marginTop: 8 }}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Reply…"
                rows={3}
                style={{
                  width: "100%",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: 8,
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
                data-testid="discovery-input"
              />
              <button
                type="button"
                className="btn primary"
                onClick={onSend}
                disabled={busy || !text.trim()}
                style={{ marginTop: 6 }}
                data-testid="discovery-send"
              >
                Send
              </button>
              {error && (
                <span
                  role="alert"
                  style={{ color: "var(--danger)", fontSize: 12, marginLeft: 8 }}
                >
                  {error}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="pd-right">
          <div className="pd-canv-tabs">
            <button type="button" className="pd-canv-tab active">
              <span className="d ok" /> Primary plan
            </button>
            <button type="button" className="pd-canv-tab">+ Alternate scenario</button>
            <div style={{ flex: 1 }} />
            <span className="pd-canv-tools">⊕ ⊖ ⚶ ⌗</span>
          </div>
          <div className="pd-canvas">
            <div className="pd-canv-lbl">
              <span className="pulse-dot" />
              {`Playbook materializing · ${draftSteps.length} steps drafted`}
            </div>
            {draftSteps.map((s, i) => (
              <div
                className={`pd-step pd-step-${s.status} ${s.justAdded ? "pd-step-new" : ""}`}
                key={s.id}
              >
                <div className="pd-step-ord">{s.ord}</div>
                <div className="pd-step-b">
                  <div className="pd-step-h">
                    <span className="pd-step-t">{s.title}</span>
                    <span
                      className={`pd-step-type pd-type-${s.type.toLowerCase()}`}
                    >
                      {s.type}
                    </span>
                  </div>
                  <div className="pd-step-m">
                    <span>Owner: {s.owner}</span>
                    {s.justAdded && (
                      <span className="pd-step-new-tag">just added</span>
                    )}
                  </div>
                </div>
                {s.status === "drafting" && <div className="pd-step-shimmer" />}
                {i < draftSteps.length - 1 && <div className="pd-step-line" />}
              </div>
            ))}
          </div>
          <div className="pd-legend">
            <span>Legend:</span>
            {(["Task", "Decision", "Comm", "Query", "Milestone"] as const).map(
              (t) => (
                <span className="pd-legend-i" key={t}>
                  <span
                    className={`pd-legend-d pd-type-${t.toLowerCase()}`}
                  />
                  {t}
                </span>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TEMPLATE

export function TemplateStage(props: StageBaseProps) {
  const { patch, session, data, workspaceId, entryId } = useStageContext(props);
  const tQ = useTemplates();
  const planIt = usePlanIt(workspaceId);
  const [picked, setPicked] = useState<TemplateMeta | null>(null);

  useEffect(() => {
    if (!session.overrideTemplateId || picked || tQ.isLoading) return;
    const match = tQ.data?.templates.find(
      (t) => templateKey(t) === session.overrideTemplateId,
    );
    if (match) setPicked(match);
  }, [session.overrideTemplateId, tQ.data, tQ.isLoading, picked]);

  const onUseTemplate = () => {
    if (!picked) return;
    if (!entryId) {
      // Visual preview only — no backend entry. Skip to editor with fixture.
      patch({ stage: "editor", overrideTemplateId: templateKey(picked) });
      return;
    }
    planIt.mutate(
      {
        entryId,
        overrideTemplateId: templateKey(picked),
        ...(session.outcomeStatement !== null
          ? { outcomeStatement: session.outcomeStatement }
          : {}),
        ...(session.successMetrics.length > 0
          ? { successMetrics: session.successMetrics }
          : {}),
      },
      {
        onSuccess: (r) => {
          if (r.kind === "draft_play") {
            const projected = projectDraft(
              r.play as { id: string; steps?: { id: string }[]; edges?: { from: string; to: string }[] },
              { initiativeTitle: data.initiative.title },
            );
            patch({
              stage: "review",
              draftPlay: projected,
              decompositionInputId: r.decompositionInputId,
              overrideTemplateId: templateKey(picked),
            });
          }
        },
      },
    );
  };

  return (
    <div className="main-inner planner-chooser" data-testid="planner-template">
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={() => patch({ stage: "chooser" })}
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">Template · {data.initiative.title}</div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn primary"
          disabled={!picked || planIt.isPending}
          onClick={onUseTemplate}
          data-testid="template-use"
        >
          {planIt.isPending ? "Decomposing…" : "Use template →"}
        </button>
      </div>

      <div className="planner-h">
        <div className="section-eyebrow">PATH A · TEMPLATE-DRIVEN</div>
        <div className="planner-title">Pick a template</div>
        <div className="planner-sub">
          Industry × Trade × Project archetype. Playbook generates in seconds.
        </div>
      </div>

      <div className="tpl-grid" data-testid="template-grid">
        {tQ.isLoading && <div className="tpl-empty">Loading templates…</div>}
        {!tQ.isLoading &&
          (tQ.data?.templates ?? []).map((t) => {
            const k = templateKey(t);
            const on = picked && templateKey(picked) === k;
            return (
              <div
                className={`tpl-card ${on ? "primary" : ""}`}
                key={k}
                onClick={() => setPicked(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setPicked(t);
                }}
                data-testid={`template-card-${k}`}
              >
                <div className="tpl-card-h">
                  <div className="tpl-arch">{t.archetype}</div>
                  <span className="tpl-badge">v{t.version}</span>
                </div>
                <div className="tpl-card-t">{t.name}</div>
                <div className="tpl-card-meta">
                  <span>{t.industry}</span>
                  <span>·</span>
                  <span>{t.trade}</span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-3)",
                    lineHeight: 1.5,
                    margin: "8px 0 12px",
                  }}
                >
                  {t.description}
                </div>
              </div>
            );
          })}
        {!tQ.isLoading && (tQ.data?.templates ?? []).length === 0 && (
          <div className="tpl-empty">No templates available.</div>
        )}
      </div>

      {planIt.error && (
        <div
          data-testid="template-error"
          style={{ marginTop: 12, color: "var(--danger)", fontSize: 12 }}
        >
          {planIt.error.message}
        </div>
      )}
    </div>
  );
}

function templateKey(t: TemplateMeta): string {
  return `${t.industry}/${t.trade}/${t.archetype}/${t.version}`;
}

// ────────────────────────────────────────────────────────────────────────────
// HYBRID — template + discovery refinement.
// Internally we just delegate: pick template, then drop into discovery, then
// to editor. The DiscoveryStage already handles the discovery half; we add
// a thin wrapper that picks the template first.

export function HybridStage(props: StageBaseProps) {
  const { session } = useStageContext(props);
  // If we haven't picked an override template yet, show the template picker
  // labeled as Hybrid step 1; once chosen we drop into Discovery.
  if (!session.overrideTemplateId) {
    return <TemplateStage {...props} />;
  }
  return <DiscoveryStage {...props} hybrid />;
}

// ────────────────────────────────────────────────────────────────────────────
// EDITOR (Timeline + Graph + Kanban + Inspector)

const SWIM_LABELS: Record<0 | 1 | 2, string> = {
  0: "Execution",
  1: "Intel & dependencies",
  2: "Approvals & milestones",
};

function StepInspector({
  step,
  steps,
  workspaceId,
  onSelect,
  onStepUpdate,
}: {
  step: PlannerStep;
  steps: PlannerStep[];
  workspaceId: string;
  onSelect: (id: string) => void;
  onStepUpdate: (stepId: string, updates: Partial<PlannerStep>) => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [smartMatchOpen, setSmartMatchOpen] = useState(false);
  const stepById = useMemo(() => {
    const m = new Map<string, PlannerStep>();
    for (const s of steps) m.set(s.id, s);
    return m;
  }, [steps]);
  const downstream = steps.filter((x) => (x.deps || []).includes(step.id));
  return (
    <div className="ed-inspector" data-testid="step-inspector">
      <div className="ed-insp-h">
        <span className="ed-insp-n">Step {step.ord}</span>
        <span className={`ed-tl-step-type pd-type-${step.type.toLowerCase()}`}>
          {step.type}
        </span>
        {step.gate && <span className="pill amber">Decision gate</span>}
        {step.alternate && <span className="pill purple">Alternate path</span>}
      </div>
      <div className="ed-insp-t">{step.title}</div>
      <div className="ed-insp-meta">
        <div className="ed-insp-mr">
          <span className="ed-insp-ml">Owner</span>
          <span>{step.owner}</span>
        </div>
        <div className="ed-insp-mr">
          <span className="ed-insp-ml">Window</span>
          <span>
            Day {step.start}–{step.end} · {step.end - step.start}d
          </span>
        </div>
        <div className="ed-insp-mr">
          <span className="ed-insp-ml">Mode</span>
          <span>
            {step.autonomous
              ? "Autonomous (agent runs unattended)"
              : "Requires claim"}
          </span>
        </div>
      </div>
      <div className="ed-insp-h2">Depends on</div>
      <div>
        {(step.deps || []).length === 0 ? (
          <div className="ed-insp-empty-l">
            No prerequisites — can start at kickoff
          </div>
        ) : (
          (step.deps || []).map((id) => {
            const d = stepById.get(id);
            return (
              d && (
                <div
                  className="ed-insp-dep"
                  key={id}
                  onClick={() => onSelect(id)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="ed-insp-dep-n">{d.ord}</span>
                  <span>{d.title}</span>
                </div>
              )
            );
          })
        )}
      </div>
      <div className="ed-insp-h2">Unlocks</div>
      <div>
        {downstream.length === 0 ? (
          <div className="ed-insp-empty-l">Terminal step</div>
        ) : (
          downstream.map((d) => (
            <div
              className="ed-insp-dep"
              key={d.id}
              onClick={() => onSelect(d.id)}
              role="button"
              tabIndex={0}
            >
              <span className="ed-insp-dep-n">{d.ord}</span>
              <span>{d.title}</span>
            </div>
          ))
        )}
      </div>
      <div className="ed-insp-act">
        <button
          type="button"
          className="btn primary"
          onClick={() => setSmartMatchOpen(true)}
          style={{ display: "flex", alignItems: "center", gap: 5 }}
        >
          ⚡ Smart match
        </button>
        <button type="button" className="btn" onClick={() => setEditOpen(true)}>Manual</button>
      </div>

      {editOpen && (
        <StepEditModal
          workspaceId={workspaceId}
          stepTitle={step.title}
          currentOwner={step.owner}
          currentHandId={null}
          currentBindingId={null}
          isAutonomous={step.autonomous ?? false}
          onSave={({ owner, autonomous }) => {
            onStepUpdate(step.id, { owner, autonomous });
            setEditOpen(false);
          }}
          onClose={() => setEditOpen(false)}
        />
      )}

      {smartMatchOpen && (
        <AgentMatchModal
          stepId={step.id}
          stepTitle={step.title}
          stepType={step.type}
          workspaceId={workspaceId}
          onClose={() => setSmartMatchOpen(false)}
          onAssigned={() => {
            setSmartMatchOpen(false);
            onStepUpdate(step.id, { autonomous: true });
          }}
        />
      )}
    </div>
  );
}

function InspectorHint() {
  return (
    <div
      className="ed-inspector ed-inspector-empty"
      data-testid="step-inspector-empty"
    >
      <div className="ed-insp-empty-t">Step inspector</div>
      <div className="ed-insp-empty-s">
        Click any step in the timeline to inspect type, owner, dependencies,
        and agent binding.
      </div>
      <div className="ed-insp-legend">
        <div className="ed-insp-legend-h">Legend</div>
        {(["Task", "Decision", "Comm", "Query", "Wait", "Milestone"] as const).map(
          (t) => (
            <div className="ed-insp-leg-i" key={t}>
              <span className={`pd-legend-d pd-type-${t.toLowerCase()}`} />
              {t}
            </div>
          ),
        )}
        <div className="ed-insp-leg-h2">Symbols</div>
        <div className="ed-insp-leg-i">
          <span className="ed-insp-leg-sym">⚡</span>
          Autonomous step (no claim needed)
        </div>
        <div className="ed-insp-leg-i">
          <span className="ed-insp-leg-sym">◈</span>
          Decision gate — blocks downstream
        </div>
      </div>
    </div>
  );
}

interface TimelineProps {
  full: PlannerPlaybook;
  selectedStepId: string | null;
  showDeps: boolean;
  onSelect: (id: string) => void;
}

// ─── List view — default. Mirrors the play detail Steps tab so the
//     editor's edit surface stays consistent with where the user
//     came from (clicking through a play card → "Edit" lands in the
//     planner; rendering steps as a vertical list matches expectation
//     and avoids the gantt-style timeline as a default).
interface ListProps {
  full: PlannerPlaybook;
  selectedStepId: string | null;
  onSelect: (id: string) => void;
  onMatchAgent?: (stepId: string, stepTitle: string, stepType: string) => void;
}

function ListView({ full, selectedStepId, onSelect, onMatchAgent }: ListProps) {
  const steps = full.steps;
  if (steps.length === 0) {
    return (
      <div
        data-testid="editor-list"
        style={{
          padding: "32px 16px",
          color: "var(--text-3)",
          fontSize: 13,
          textAlign: "center",
        }}
      >
        No steps yet. Pick a template or describe an outcome to populate the play.
      </div>
    );
  }
  return (
    <div data-testid="editor-list" style={{ padding: "8px 0", display: "flex", flexDirection: "column", gap: 8 }}>
      {steps.map((s) => {
        const isSelected = s.id === selectedStepId;
        const typeLabel = s.type ?? "task";
        return (
          <div
            key={s.id}
            data-testid={`editor-list-row-${s.id}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(s.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSelect(s.id);
            }}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              padding: "12px 14px",
              borderRadius: 8,
              border: `1.5px solid ${isSelected ? "var(--accent, #6366f1)" : "var(--border)"}`,
              background: isSelected
                ? "color-mix(in srgb, var(--accent, #6366f1) 4%, var(--bg))"
                : "var(--bg)",
              cursor: "pointer",
              transition: "border-color 120ms, background 120ms",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: "var(--bg-soft)",
                color: "var(--text-2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {s.ord}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text-1)" }}>{s.title}</span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: "var(--text-3)",
                    background: "var(--bg-soft)",
                    padding: "2px 6px",
                    borderRadius: 4,
                    textTransform: "uppercase",
                  }}
                >
                  {typeLabel}
                </span>
              </div>
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-3)" }}>
                <span>{s.owner ?? (s.autonomous ? "agent" : "—")}</span>
                {s.autonomous && onMatchAgent ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMatchAgent(s.id, s.title, s.type ?? "task");
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 8px",
                      borderRadius: 99,
                      border: "1px dashed var(--accent, #6366f1)",
                      background: "color-mix(in srgb, var(--accent, #6366f1) 8%, transparent)",
                      color: "var(--accent, #6366f1)",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    ⚡ Match agent
                  </button>
                ) : null}
                {s.gate ? (
                  <span style={{ fontSize: 11, color: "var(--accent, #6366f1)" }}>◆ gate</span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimelineView({
  full,
  selectedStepId,
  showDeps,
  onSelect,
}: TimelineProps) {
  const totalDays = Math.max(
    14,
    full.steps.reduce((m, s) => Math.max(m, s.end), 14),
  );
  const SWIM_H = 78;
  const COL_W = 26;
  const LEFT_PAD = 12;
  const dayToX = (d: number) => LEFT_PAD + d * COL_W;
  const stepById = new Map(full.steps.map((s) => [s.id, s]));

  return (
    <div className="ed-tl-wrap" data-testid="editor-timeline">
      <div
        className="ed-tl-phase"
        style={{ width: dayToX(totalDays) + 20 }}
      >
        {full.phases.map((p) => (
          <div
            className={`ed-tl-phase-i ${p.color}`}
            style={{ left: dayToX(p.start), width: (p.end - p.start) * COL_W }}
            key={p.id}
          >
            {p.label}
          </div>
        ))}
      </div>

      <div className="ed-tl-axis" style={{ width: dayToX(totalDays) + 20 }}>
        {Array.from(
          { length: Math.floor(totalDays / 7) + 1 },
          (_, i) => i * 7,
        ).map((d) => (
          <div className="ed-tl-week" style={{ left: dayToX(d) }} key={d}>
            Wk {Math.floor(d / 7) + 1}
          </div>
        ))}
      </div>

      <div
        className="ed-tl-lanes"
        style={{ width: dayToX(totalDays) + 20, height: SWIM_H * 3 }}
      >
        {([0, 1, 2] as const).map((i) => (
          <div
            className="ed-tl-lane"
            style={{ top: i * SWIM_H, height: SWIM_H }}
            key={i}
          >
            <div className="ed-tl-lane-lbl">{SWIM_LABELS[i]}</div>
          </div>
        ))}
        {Array.from(
          { length: Math.floor(totalDays / 7) + 1 },
          (_, i) => i * 7,
        ).map((d) => (
          <div
            className="ed-tl-grid"
            style={{ left: dayToX(d), height: SWIM_H * 3 }}
            key={d}
          />
        ))}

        {showDeps && (
          <svg
            className="ed-tl-deps"
            width={dayToX(totalDays) + 20}
            height={SWIM_H * 3}
          >
            <defs>
              <marker
                id="arr"
                viewBox="0 0 10 10"
                refX={8}
                refY={5}
                markerWidth={6}
                markerHeight={6}
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 z" fill="#b0afa9" />
              </marker>
            </defs>
            {full.steps.flatMap((s) =>
              (s.deps || []).map((depId) => {
                const dep = stepById.get(depId);
                if (!dep) return null;
                const x1 = dayToX(dep.end);
                const y1 = dep.swim * SWIM_H + SWIM_H / 2;
                const x2 = dayToX(s.start);
                const y2 = s.swim * SWIM_H + SWIM_H / 2;
                const midX = (x1 + x2) / 2;
                return (
                  <path
                    key={`${dep.id}-${s.id}`}
                    d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2 - 4} ${y2}`}
                    stroke="#b0afa9"
                    strokeWidth={1}
                    fill="none"
                    strokeDasharray={s.blocked ? "3,3" : "0"}
                    markerEnd="url(#arr)"
                    opacity={0.55}
                  />
                );
              }),
            )}
          </svg>
        )}

        {full.steps.map((s) => {
          const left = dayToX(s.start);
          const width = (s.end - s.start) * COL_W;
          const top = s.swim * SWIM_H + 14;
          const isSelected = s.id === selectedStepId;
          return (
            <div
              key={s.id}
              className={`ed-tl-step ed-tl-type-${s.type.toLowerCase()} ${
                s.gate ? "ed-tl-gate" : ""
              } ${s.alternate ? "ed-tl-alt" : ""} ${
                s.blocked ? "ed-tl-blocked" : ""
              } ${isSelected ? "ed-tl-selected" : ""}`}
              style={{ left, width, top }}
              onClick={() => onSelect(s.id)}
              role="button"
              tabIndex={0}
              data-testid={`tl-step-${s.id}`}
            >
              <div className="ed-tl-step-h">
                <span className="ed-tl-step-n">{s.ord}</span>
                <span className="ed-tl-step-t">{s.title}</span>
                {s.autonomous && (
                  <span
                    className="ed-tl-step-auto"
                    title="Autonomous"
                  >
                    ⚡
                  </span>
                )}
              </div>
              <div className="ed-tl-step-m">
                <span
                  className={`ed-tl-step-type pd-type-${s.type.toLowerCase()}`}
                >
                  {s.type}
                </span>
                <span className="ed-tl-step-o">{s.owner}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="ed-tl-today"
        style={{ left: dayToX(0), height: SWIM_H * 3 + 60 }}
      >
        <span className="ed-tl-today-l">Kickoff</span>
      </div>
    </div>
  );
}

interface GraphProps {
  full: PlannerPlaybook;
  selectedStepId: string | null;
  onSelect: (id: string) => void;
}

function GraphView({ full, selectedStepId, onSelect }: GraphProps) {
  const stepById = new Map(full.steps.map((s) => [s.id, s]));
  const col = new Map<string, number>();
  const computeCol = (id: string, stack = new Set<string>()): number => {
    const cached = col.get(id);
    if (cached !== undefined) return cached;
    if (stack.has(id)) return 0;
    stack.add(id);
    const s = stepById.get(id);
    if (!s || s.deps.length === 0) {
      col.set(id, 0);
      return 0;
    }
    const c = Math.max(...s.deps.map((d) => computeCol(d, stack))) + 1;
    col.set(id, c);
    return c;
  };
  for (const s of full.steps) computeCol(s.id);
  const maxCol = Math.max(0, ...col.values());

  const buckets = new Map<string, PlannerStep[]>();
  for (const s of full.steps) {
    const k = `${col.get(s.id) ?? 0}_${s.swim}`;
    const arr = buckets.get(k) ?? [];
    arr.push(s);
    buckets.set(k, arr);
  }

  const COL_W = 200;
  const ROW_H = 130;
  const NODE_W = 168;
  const NODE_H = 72;
  const PAD = 36;

  const pos = new Map<string, { x: number; y: number }>();
  for (const s of full.steps) {
    const k = `${col.get(s.id) ?? 0}_${s.swim}`;
    const bucket = buckets.get(k) ?? [];
    const idx = bucket.indexOf(s);
    const ox = (idx - (bucket.length - 1) / 2) * 14;
    pos.set(s.id, {
      x: PAD + (col.get(s.id) ?? 0) * COL_W + ox,
      y: PAD + 30 + s.swim * ROW_H + (idx % 2 === 0 ? 0 : 8),
    });
  }
  const totalW = PAD * 2 + (maxCol + 1) * COL_W;
  const totalH = PAD + 30 + 3 * ROW_H + 20;

  return (
    <div className="ed-graph-wrap" data-testid="editor-graph">
      <div className="ed-graph-legend">
        <span>Process diagram</span>
        <span className="ed-graph-legend-s">
          Nodes = steps, edges = dependencies. Columns = execution depth. Rows
          = swimlanes.
        </span>
      </div>
      <div
        className="ed-graph-canvas"
        style={{ minWidth: totalW, height: totalH }}
      >
        {([0, 1, 2] as const).map((i) => (
          <div
            className="ed-graph-swim"
            style={{
              top: PAD + 30 + i * ROW_H - 12,
              height: ROW_H - 4,
              width: totalW - 8,
            }}
            key={i}
          >
            <span className="ed-graph-swim-l">{SWIM_LABELS[i]}</span>
          </div>
        ))}
        <svg className="ed-graph-svg" width={totalW} height={totalH}>
          <defs>
            <marker
              id="g-arr"
              viewBox="0 0 10 10"
              refX={8}
              refY={5}
              markerWidth={7}
              markerHeight={7}
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="#9a958c" />
            </marker>
          </defs>
          {full.steps.flatMap((s) =>
            (s.deps || []).map((depId) => {
              const a = pos.get(depId);
              const b = pos.get(s.id);
              if (!a || !b) return null;
              const x1 = a.x + NODE_W;
              const y1 = a.y + NODE_H / 2;
              const x2 = b.x;
              const y2 = b.y + NODE_H / 2;
              const cx = (x1 + x2) / 2;
              const isSel =
                selectedStepId === s.id || selectedStepId === depId;
              return (
                <path
                  key={`${depId}-${s.id}`}
                  d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2 - 5} ${y2}`}
                  stroke={isSel ? "var(--accent)" : "#b0afa9"}
                  strokeWidth={isSel ? 1.8 : 1.2}
                  fill="none"
                  strokeDasharray={s.blocked ? "4,3" : "0"}
                  markerEnd="url(#g-arr)"
                  opacity={isSel ? 0.9 : 0.5}
                />
              );
            }),
          )}
        </svg>
        {full.steps.map((s) => {
          const p = pos.get(s.id);
          if (!p) return null;
          const isSel = s.id === selectedStepId;
          const style: CSSProperties = {
            left: p.x,
            top: p.y,
            width: NODE_W,
            height: NODE_H,
          };
          return (
            <div
              key={s.id}
              className={`ed-graph-node ed-tl-type-${s.type.toLowerCase()} ${
                s.gate ? "ed-tl-gate" : ""
              } ${s.alternate ? "ed-tl-alt" : ""} ${
                s.blocked ? "ed-tl-blocked" : ""
              } ${isSel ? "ed-tl-selected" : ""}`}
              style={style}
              onClick={() => onSelect(s.id)}
              role="button"
              tabIndex={0}
              data-testid={`graph-node-${s.id}`}
            >
              <div className="ed-graph-node-h">
                <span className="ed-tl-step-n">{s.ord}</span>
                <span
                  className={`ed-tl-step-type pd-type-${s.type.toLowerCase()}`}
                >
                  {s.type}
                </span>
                {s.autonomous && (
                  <span className="ed-tl-step-auto">⚡</span>
                )}
                {s.gate && (
                  <span
                    className="ed-graph-gate"
                    title="Decision gate"
                  >
                    ◈
                  </span>
                )}
              </div>
              <div className="ed-graph-node-t">{s.title}</div>
              <div className="ed-graph-node-o">{s.owner}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface KanbanProps {
  full: PlannerPlaybook;
  selectedStepId: string | null;
  onSelect: (id: string) => void;
}

function KanbanView({ full, selectedStepId, onSelect }: KanbanProps) {
  const cols = full.phases.map((p) => ({
    ...p,
    steps: full.steps.filter((s) => s.start >= p.start && s.start < p.end),
  }));
  return (
    <div className="ed-kanban" data-testid="editor-kanban">
      {cols.map((c) => (
        <div className={`ed-kb-col ${c.color}`} key={c.id}>
          <div className="ed-kb-col-h">
            <span>{c.label}</span>
            <span className="ed-kb-col-c">{c.steps.length}</span>
          </div>
          <div className="ed-kb-col-b">
            {c.steps.map((s) => (
              <div
                key={s.id}
                className={`ed-kb-step ed-tl-type-${s.type.toLowerCase()} ${
                  s.id === selectedStepId ? "ed-kb-selected" : ""
                }`}
                onClick={() => onSelect(s.id)}
                role="button"
                tabIndex={0}
                data-testid={`kb-step-${s.id}`}
              >
                <div className="ed-kb-step-h">
                  <span
                    className={`ed-tl-step-type pd-type-${s.type.toLowerCase()}`}
                  >
                    {s.type}
                  </span>
                  {s.autonomous && (
                    <span className="ed-tl-step-auto">⚡</span>
                  )}
                  {s.gate && (
                    <span
                      className="ed-kb-gate"
                      title="Decision gate"
                    >
                      ◈
                    </span>
                  )}
                </div>
                <div className="ed-kb-step-t">
                  {s.ord}. {s.title}
                </div>
                <div className="ed-kb-step-o">{s.owner}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function EditorStage(props: StageBaseProps) {
  const { patch, data, session, workspaceId } = useStageContext(props);
  const navigate = useNavigate();

  // Preview mode: when the Templates catalog launched us here with a
  // template selected (`overrideTemplateId`) but no real entry/draft,
  // fetch the template's step graph from the registry so the canvas
  // shows the actual template content instead of the fixture. Read-only:
  // user can't save or launch without first committing the template
  // against a roadmap entry via "Use this template".
  const isPreviewMode =
    !props.entryId && !session.draftPlay && !!session.overrideTemplateId;

  // Direct template → play creator, used by the preview-mode "Use this
  // template" button. Mints an auto roadmap entry + decomposes in one
  // request, lands the user on the new play.
  const fromTemplateMut = useFromTemplate(workspaceId);

  // When entering preview mode, ensure the view starts on the card list.
  // Without this, users with a persisted "timeline" preference from an
  // earlier visit land on the timeline canvas — which the user explicitly
  // pushed back on for previews. Only force on entry; the user can still
  // switch to timeline/graph/kanban afterwards and the choice persists.
  useEffect(() => {
    if (isPreviewMode && session.editorView !== "list") {
      patch({ editorView: "list" });
    }
    // We intentionally fire only when isPreviewMode flips on, so we
    // don't fight the user every time they pick a different view.
    // patch is stable (memoised in usePlannerSession via useCallback).
  }, [isPreviewMode, patch]);

  const previewKey = isPreviewMode
    ? parseTemplateKey(session.overrideTemplateId)
    : null;
  const previewQ = useTemplatePreview(
    previewKey?.industry,
    previewKey?.trade,
    previewKey?.archetype,
  );

  // Project the template preview into a PlannerPlaybook so the canvas
  // renders it the same way it'd render a real draft. Falls back to the
  // existing data.fullPlaybook (real draft or fixture) when not in
  // preview mode.
  const full = useMemo(() => {
    if (isPreviewMode) {
      // Loaded preview → render it.
      if (previewQ.data) {
        const p = previewQ.data;
        return projectDraft(
          {
            steps: p.graph.nodes.map((n) => ({
              id: n.id,
              title: n.title,
              ...(n.agentCapability ? { agentCapability: n.agentCapability } : {}),
            })),
            edges: p.graph.edges,
          },
          { initiativeTitle: p.meta.name },
        );
      }
      // Loading or error → return an EMPTY projected playbook with the
      // template's name (if we know it). Critically, do NOT fall back to
      // data.fullPlaybook in preview mode — that's the demo fixture
      // ("Plan a new play · 12 steps · Channel motion · …"), which users
      // mistook for the actual template's content. Empty + a loading or
      // error banner is far clearer than misleading dummy data.
      const fallbackTitle = previewKey
        ? `${previewKey.industry}/${previewKey.trade}/${previewKey.archetype}`
        : "Template preview";
      return projectDraft(null, { initiativeTitle: fallbackTitle });
    }
    return data.fullPlaybook;
  }, [isPreviewMode, previewQ.data, previewKey, data.fullPlaybook]);

  // Preview-specific status: surface loading + error states in the
  // banner so the user knows why the canvas is empty rather than
  // assuming the template is broken.
  const previewStatus: { kind: "loading" | "error"; message: string } | null = (() => {
    if (!isPreviewMode) return null;
    if (previewQ.isLoading) return { kind: "loading", message: "Loading template…" };
    if (previewQ.error) {
      const err = previewQ.error as unknown as { body?: { message?: string }; message?: string };
      const msg = err.body?.message ?? err.message ?? "Couldn't load template preview.";
      return { kind: "error", message: msg };
    }
    return null;
  })();

  const selected = full.steps.find((s) => s.id === session.selectedStepId);
  const [savedFlash, setSavedFlash] = useState(false);
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (saveDraftTimerRef.current !== null) clearTimeout(saveDraftTimerRef.current); }, []);

  const handleStepUpdate = useCallback((stepId: string, updates: Partial<PlannerStep>) => {
    if (isPreviewMode) return; // preview is read-only
    const updatedSteps = full.steps.map((s) => s.id === stepId ? { ...s, ...updates } : s);
    patch({ draftPlay: { ...full, steps: updatedSteps } });
  }, [full, patch, isPreviewMode]);

  const handleBack = useCallback(() => {
    // In preview mode (entered from Templates catalog), back goes back
    // to the templates list. Otherwise back to the play we came from.
    // Without this, the back arrow on a template preview was navigating
    // to /plays/ (empty playId) which 404'd / dumped users on a stray
    // page.
    if (isPreviewMode) {
      navigate(`/workspaces/${workspaceId}/templates`);
      return;
    }
    const playId = props.playId ?? props.entryId;
    navigate(`/workspaces/${workspaceId}/plays/${playId}`);
  }, [navigate, workspaceId, props.playId, props.entryId, isPreviewMode]);

  const handleSaveDraft = useCallback(() => {
    // Session is already auto-persisted to localStorage via Zustand.
    // Show a brief confirmation so the user knows it's saved.
    if (saveDraftTimerRef.current !== null) clearTimeout(saveDraftTimerRef.current);
    setSavedFlash(true);
    saveDraftTimerRef.current = setTimeout(() => setSavedFlash(false), 2000);
  }, []);

  return (
    <div className="planner-editor" data-testid="planner-editor">
      <OutcomeHeader
        outcomeStatement={session.outcomeStatement}
        successMetrics={session.successMetrics}
      />
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={handleBack}
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">
          Editor ·{" "}
          <span style={{ color: "var(--text-3)" }}>{full.title}</span>
        </div>
        <div style={{ flex: 1 }} />
        <div className="ed-view-toggle">
          {(["list", "timeline", "graph", "kanban"] as const).map((v) => (
            <button
              type="button"
              key={v}
              className={`ed-vt-btn ${session.editorView === v ? "active" : ""}`}
              onClick={() => patch({ editorView: v })}
              data-testid={`editor-view-${v}`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        {session.editorView === "timeline" && (
          <label className="ed-deps-toggle">
            <input
              type="checkbox"
              checked={session.showDeps}
              onChange={(e) => patch({ showDeps: e.target.checked })}
              data-testid="editor-deps-toggle"
            />
            <span>Show dependencies</span>
          </label>
        )}
        {isPreviewMode ? (
          <button
            type="button"
            className="btn primary"
            onClick={async () => {
              // Direct template→play: mint an auto roadmap entry +
              // decompose + redirect to the new play. Same flow as the
              // templates catalog's "Use this template" button.
              const key = session.overrideTemplateId;
              if (!key) return;
              const parts = key.split("/");
              if (parts.length < 3) return;
              try {
                const r = await fromTemplateMut.mutateAsync({
                  industry: parts[0]!,
                  trade: parts[1]!,
                  archetype: parts[2]!,
                });
                if (r.kind === "draft_play" || r.kind === "existing_play") {
                  navigate(`/workspaces/${workspaceId}/plays/${r.playId}`);
                  return;
                }
                navigate(`/workspaces/${workspaceId}/inbox?with_template=${encodeURIComponent(key)}`);
              } catch {
                navigate(`/workspaces/${workspaceId}/inbox?with_template=${encodeURIComponent(key)}`);
              }
            }}
            disabled={fromTemplateMut.isPending}
            data-testid="editor-use-template"
            title="Create a play directly from this template"
          >
            {fromTemplateMut.isPending ? "Creating play…" : "Use this template →"}
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={handleSaveDraft}>
              {savedFlash ? "✓ Saved" : "Save draft"}
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => patch({ stage: "review" })}
              data-testid="editor-review"
            >
              Review &amp; launch →
            </button>
          </>
        )}
      </div>

      {isPreviewMode ? (
        <div
          data-testid="editor-preview-banner"
          style={{
            margin: "0 16px",
            padding: "8px 12px",
            borderRadius: 6,
            background: previewStatus?.kind === "error"
              ? "color-mix(in srgb, #dc2626 8%, transparent)"
              : "color-mix(in srgb, var(--accent, #6366f1) 8%, transparent)",
            border: previewStatus?.kind === "error"
              ? "1px solid color-mix(in srgb, #dc2626 40%, transparent)"
              : "1px solid color-mix(in srgb, var(--accent, #6366f1) 30%, transparent)",
            fontSize: 12,
            color: previewStatus?.kind === "error" ? "#b91c1c" : "var(--text-2)",
          }}
        >
          {previewStatus?.kind === "loading" ? (
            <>
              <strong>Loading preview…</strong> · fetching the template's step graph from the library.
            </>
          ) : previewStatus?.kind === "error" ? (
            <>
              <strong>Preview unavailable</strong> · {previewStatus.message}
            </>
          ) : (
            <>
              <strong>Preview</strong> · this is the template's default step graph with no entry attached. Pick a roadmap entry via <em>Use this template</em> to edit or launch.
            </>
          )}
        </div>
      ) : null}

      <div className="ed-body">
        <div className="ed-left">
          <div className="ed-sum">
            <div className="section-eyebrow">PLAYBOOK SUMMARY</div>
            <div className="ed-sum-t">{full.title}</div>
            <div className="ed-sum-s">{full.summary}</div>
            <div className="ed-sum-k">
              <div className="ed-sum-r">
                <span className="ed-sum-l">Target</span>
                <span className="ed-sum-v">{full.kpiTarget}</span>
              </div>
              <div className="ed-sum-r">
                <span className="ed-sum-l">Window</span>
                <span className="ed-sum-v">{full.window}</span>
              </div>
            </div>
          </div>
          <div className="ed-card ed-pl">
            <div className="section-eyebrow">PATTERN LIBRARY MATCH</div>
            {full.patternLibrary ? (
              <>
                <div className="ed-pl-t">{full.patternLibrary.match}</div>
                <div className="ed-pl-p">
                  <span className="pill purple">
                    {full.patternLibrary.priors} priors
                  </span>
                  {" · "}
                  {full.patternLibrary.confidence}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                No pattern match yet — add steps to find similar plays.
              </div>
            )}
          </div>
          <div className="ed-card">
            <div className="section-eyebrow">RISKS XAMI SURFACED</div>
            {full.risks.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                No risks surfaced yet.
              </div>
            ) : (
              full.risks.map((r) => (
                <div className={`ed-risk ed-risk-${r.severity}`} key={r.id}>
                  <span className="ed-risk-d" />
                  <span className="ed-risk-t">{r.text}</span>
                </div>
              ))
            )}
          </div>
          <div className="ed-card">
            <div className="section-eyebrow">AGENT COVERAGE</div>
            {full.agentBindings.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                No agent bindings yet.
              </div>
            ) : (
              full.agentBindings.map((b, i) => (
                <div className={`ed-bind ed-bind-${b.status}`} key={i}>
                  <span className="ed-bind-c">{b.capability}</span>
                  <span className="ed-bind-a">{b.agent}</span>
                  {b.warn && <span className="ed-bind-w">{b.warn}</span>}
                  {b.status === "gap" && (
                    <a className="ed-bind-b">+ Bind</a>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ed-center">
          {session.editorView === "list" && (
            <ListView
              full={full}
              selectedStepId={session.selectedStepId}
              onSelect={(id) => patch({ selectedStepId: id })}
            />
          )}
          {session.editorView === "timeline" && (
            <TimelineView
              full={full}
              selectedStepId={session.selectedStepId}
              showDeps={session.showDeps}
              onSelect={(id) => patch({ selectedStepId: id })}
            />
          )}
          {session.editorView === "graph" && (
            <GraphView
              full={full}
              selectedStepId={session.selectedStepId}
              onSelect={(id) => patch({ selectedStepId: id })}
            />
          )}
          {session.editorView === "kanban" && (
            <KanbanView
              full={full}
              selectedStepId={session.selectedStepId}
              onSelect={(id) => patch({ selectedStepId: id })}
            />
          )}
        </div>

        <div className="ed-right">
          {selected ? (
            <StepInspector
              step={selected}
              steps={full.steps}
              workspaceId={workspaceId}
              onSelect={(id) => patch({ selectedStepId: id })}
              onStepUpdate={handleStepUpdate}
            />
          ) : (
            <InspectorHint />
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// REVIEW

interface ApproveStartArgs {
  workspaceId: string;
  playId: string;
}

function useApproveAndStart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workspaceId, playId }: ApproveStartArgs) => {
      await apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/approve`,
        {},
      );
      await apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/start`,
        {},
      );
      return { playId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plays"] });
    },
  });
}

export function ReviewStage(props: StageBaseProps) {
  const { patch, data, reset, workspaceId, entryId, session } =
    useStageContext(props);
  const navigate = useNavigate();
  const planIt = usePlanIt(workspaceId);
  const approveAndStart = useApproveAndStart();
  const [error, setError] = useState<string | null>(null);
  const full = data.fullPlaybook;
  const stepCount = full.steps.length;
  const gateCount = full.steps.filter((s) => s.gate).length;
  const agentCount = full.agentBindings.filter((b) => b.status === "bound")
    .length;

  const checks: { ok: boolean; warn: boolean; h: string; d: string }[] = [
    {
      ok: !!full.patternLibrary,
      warn: !full.patternLibrary,
      h: "Pattern Library match",
      d: full.patternLibrary?.confidence ?? "No match yet — add steps first",
    },
    {
      ok: true,
      warn: false,
      h: "Stakeholders confirmed",
      d: `${data.quickLaunch.stakeholders.length} stakeholders mapped`,
    },
    { ok: true, warn: false, h: "KPI target set", d: full.kpiTarget },
    (() => {
      const gaps = full.agentBindings.filter((b) => b.status === "gap");
      const ok = gaps.length === 0;
      const warn = gaps.length > 0;
      const h = ok
        ? "Agent coverage complete"
        : `${gaps.length} capability gap${gaps.length === 1 ? "" : "s"}`;
      const d = ok
        ? "All bindings in place"
        : `${gaps.map((b) => b.capability).join(", ")} — manual fallback for affected steps`;
      return { ok, warn, h, d };
    })(),
    {
      ok: true,
      warn: false,
      h: "Decision gates",
      d: `${gateCount} gates`,
    },
  ];

  const onLaunch = async () => {
    setError(null);
    if (!entryId) {
      // Visual-only preview — nothing to launch. Treat as a no-op.
      setError(
        "No roadmap entry attached. Open Planner from the inbox to launch.",
      );
      return;
    }
    try {
      // If we don't yet have a draft (user came straight from chooser →
      // editor → review without going through quick), kick off decompose
      // first so we have a play.id to approve+start.
      let playId: string | null = null;
      if (!session.draftPlay) {
        const r: PlanItResult = await planIt.mutateAsync({ entryId });
        if (r.kind !== "draft_play") {
          setError("Discovery required — please use the Discovery path.");
          return;
        }
        const projected = projectDraft(
          r.play as { id: string; steps?: { id: string }[]; edges?: { from: string; to: string }[] },
          { initiativeTitle: data.initiative.title },
        );
        patch({
          draftPlay: projected,
          decompositionInputId: r.decompositionInputId,
        });
        playId = r.play.id;
      } else {
        // We already have a projected draft; the play.id was carried in
        // session.decompositionInputId is _not_ the play.id, so we re-run
        // decompose if we don't have it cached. Simpler: persist play.id
        // in session when we get it. For now if missing, kick decompose.
        const r: PlanItResult = await planIt.mutateAsync({ entryId });
        if (r.kind !== "draft_play") {
          setError("Discovery required — please use the Discovery path.");
          return;
        }
        playId = r.play.id;
      }

      if (!playId) {
        setError("Failed to obtain draft play id.");
        return;
      }
      await approveAndStart.mutateAsync({ workspaceId, playId });
      reset();
      navigate(`/workspaces/${workspaceId}/plays/${playId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const launching = planIt.isPending || approveAndStart.isPending;

  return (
    <div className="planner-review" data-testid="planner-review">
      <OutcomeHeader
        outcomeStatement={session.outcomeStatement}
        successMetrics={session.successMetrics}
      />
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={() => patch({ stage: "editor" })}
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">Review &amp; launch</div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn">Save as draft</button>
        <button
          type="button"
          className="btn primary"
          onClick={onLaunch}
          disabled={launching}
          data-testid="review-launch"
        >
          {launching ? "Launching…" : "Launch play →"}
        </button>
      </div>

      <div className="rv-body">
        <div className="rv-h">
          <div className="section-eyebrow">FINAL REVIEW</div>
          <div className="rv-t">{full.title}</div>
          <div className="rv-s">{full.summary}</div>
          <div
            style={{
              display: "flex",
              gap: 32,
              marginTop: 16,
              fontSize: 12,
              color: "var(--text-3)",
            }}
            data-testid="review-stats"
          >
            <span>
              <b style={{ color: "var(--text)" }}>{stepCount}</b> steps
            </span>
            <span>
              <b style={{ color: "var(--text)" }}>{gateCount}</b> decision gates
            </span>
            <span>
              <b style={{ color: "var(--text)" }}>{agentCount}</b> agents bound
            </span>
            <span>
              <b style={{ color: "var(--text)" }}>{full.phases.length}</b> phases
            </span>
          </div>
        </div>

        <div className="rv-checks">
          {checks.map((c, i) => (
            <div className={`rv-check ${c.warn ? "warn" : "ok"}`} key={i}>
              <span className="rv-check-i">{c.ok ? "✓" : "⚠"}</span>
              <div>
                <div className="rv-check-h">{c.h}</div>
                <div className="rv-check-d">{c.d}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="rv-launch">
          <div>
            <div className="rv-launch-h">Ready to launch</div>
            <div className="rv-launch-s">
              On launch: play moves to Hands Ops, Step 1 starts autonomously,
              and stakeholders get briefing notifications.
            </div>
          </div>
          <button
            type="button"
            className="btn primary lg"
            onClick={onLaunch}
            disabled={launching}
          >
            {launching ? "Launching…" : "Launch play →"}
          </button>
        </div>

        {error && (
          <div
            data-testid="review-error"
            style={{ marginTop: 12, color: "var(--danger)", fontSize: 12 }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
